import React, { useCallback, useEffect, useRef, useState } from 'react';

import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';

import Button from 'components/Button';
import { ENUM_TYPE_DOWNLOAD } from 'constants/enum';
import { TEXT_COMMON, TEXT_DOWNLOAD, TEXT_REPORT_LIST } from 'constants/string/texts';
import { useClickOutsideWithSelector } from 'hooks/useClickOutside';
import { setOpenDownload } from 'store/actions/report';

const { RangePicker } = DatePicker;

const ReportListHeader = ({ options, onChange }) => {
  const dispatch = useDispatch();
  const [activeFilter, setActiveFilter] = useState(false);
  const [dateFilter, setDateFilter] = useState([]);
  const [dateFilterTemp, setDateFilterTemp] = useState([]);
  const [openPicker, setOpenPicker] = useState(false);
  const rangePickerRef = useRef(null);

  const handleItemOverview = () => {
    const from = (options.page - 1) * options.page_size + 1;
    const to = from + options.page_size - 1;
    const total = options.total;

    return `${from} - ${to > total ? total : to} / ${total} ${TEXT_REPORT_LIST.PART}`;
  };

  const onChangeDate = (dates) => {
    if (dates === null) {
      if (openPicker) {
        setDateFilterTemp([null, null]);
      } else {
        setDateFilter([null, null]);
      }
    }
  };

  const onCalendarChange = (_, dateStrings) => {
    setDateFilterTemp([...dateStrings]);
  };

  const onConfirmSelectDate = () => {
    setDateFilter(dateFilterTemp);
    setOpenPicker(false);
  };

  const submitFilter = () => {
    onChange({ from_date: dateFilter[0], to_date: dateFilter[1] });
  };

  useClickOutsideWithSelector(
    ['ant-picker-dropdown', 'range-picker-custom'],
    openPicker
      ? () => {
          setOpenPicker(false);
          setDateFilterTemp([...dateFilter]);
        }
      : () => {}
  );

  const handleFilter = useCallback(() => {
    if (options.from_date && options.to_date) {
      setDateFilter([options.from_date, options.to_date]);
    }
  }, [options]);

  useEffect(() => {
    handleFilter();
  }, [handleFilter]);

  const handleOpenDownload = (typeDownload) => {
    dispatch(
      setOpenDownload({
        isOpen: true,
        type: typeDownload,
      })
    );
  };

  return (
    <>
      <div className="flex items-end justify-between gap-2 mt-2">
        <p className="text-sm opacity-85 text-gray-336">{handleItemOverview()}</p>

        <div className="flex space-x-4">
          <Button
            onClick={() => handleOpenDownload(ENUM_TYPE_DOWNLOAD.CSV)}
            color="outline"
            text={TEXT_DOWNLOAD.CSV_DOWNLOAD}
            className="w-55.75 h-10 text-sm !px-3.75 !py-1"
            icon={<img src={require('assets/resources/FileSave.svg').default} alt="plus icon" width={14} height={14} />}
          />
          <Button
            onClick={() => handleOpenDownload(ENUM_TYPE_DOWNLOAD.EXCEL)}
            color="outline"
            text={TEXT_DOWNLOAD.EXCEL_DOWNLOAD}
            className="w-57.25 h-10 text-sm !px-3.75 !py-1"
            icon={<img src={require('assets/resources/FileSave.svg').default} alt="plus icon" width={14} height={14} />}
          />
          <Button
            className={`text-sm filter w-35.5 h-10 ${activeFilter ? 'text-primary-346 border-primary-346' : ''}`}
            onClick={() => setActiveFilter(!activeFilter)}
            icon={
              activeFilter ? (
                <div className="relative mr-2.5">
                  {options.from_date && options.to_date && <div className="w-2 h-2 absolute top-0 right-0 bg-danger-229 rounded-full"></div>}
                  <img src={require('assets/resources/FilterAltActive.svg').default} alt="plus icon" width={18} height={18} />
                </div>
              ) : (
                <div className="relative mr-2.5">
                  {options.from_date && options.to_date && <div className="w-2 h-2 absolute top-0 right-0 bg-danger-229 rounded-full"></div>}
                  <img src={require('assets/resources/FilterAlt.svg').default} alt="plus icon" width={18} height={18} />
                </div>
              )
            }
            text={TEXT_REPORT_LIST.TEXT_FILTER}
            color="outline"
          />
        </div>
      </div>

      <div className={`${activeFilter ? 'h-54.25' : 'h-0'} overflow-hidden transition-all`}>
        <div className="w-full mt-6">
          <div className="border-2 border-primary-423 p-4 rounded-md h-47.25">
            <div className="flex-between-center">
              <p className="font-bold">{TEXT_REPORT_LIST.TEXT_FILTER}</p>
              <div className="cursor-pointer" onClick={() => setActiveFilter(false)}>
                <img src={require('assets/resources/Close.svg').default} alt="close" width={24} height={24} />
              </div>
            </div>
            <p className="text-sm mt-4 mb-2">{TEXT_REPORT_LIST.ANSWER_DAY}</p>
            <div className="range-picker-custom">
              <RangePicker
                ref={rangePickerRef}
                value={
                  openPicker
                    ? [dateFilterTemp[0] ? dayjs(dateFilterTemp[0]) : null, dateFilterTemp[1] ? dayjs(dateFilterTemp[1]) : null]
                    : [dateFilter[0] ? dayjs(dateFilter[0]) : null, dateFilter[1] ? dayjs(dateFilter[1]) : null]
                }
                onClick={() => {
                  setOpenPicker(true);
                }}
                placeholder={[TEXT_COMMON.PLACEHOLDER_FROM_DATE, TEXT_COMMON.PLACEHOLDER_TO_DATE]}
                className="w-64 h-10 border-gray-651 rounded-sm"
                onChange={onChangeDate}
                onCalendarChange={onCalendarChange}
                allowEmpty
                allowClear={!openPicker}
                open={openPicker}
                autoSave={true}
                renderExtraFooter={() => {
                  return (
                    <div className="flex justify-end items-center py-2.25 px-3">
                      <Button
                        onClick={() => {
                          if (dateFilterTemp[0] && dateFilterTemp[1]) {
                            onConfirmSelectDate();
                          }
                        }}
                        className={`h-6.25 w-16 text-sm ${dateFilterTemp[0] && dateFilterTemp[1] ? 'opacity-100' : 'opacity-50 cursor-not-allowed'}`}
                        text="確定"
                      ></Button>
                    </div>
                  );
                }}
              />
            </div>
            <div className="mt-4 flex-end-center">
              <Button onClick={submitFilter} color="primary" type="button" className="w-20 h-8">
                <span className="text-sm">{TEXT_COMMON.EXECUTION}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(ReportListHeader);
